import type { ReactNode } from 'react'

export type PlanEducationIconId =
  | 'maximizingReturns'
  | 'taxOptimisations'
  | 'quarterlyPlan'
  | 'investWithConfidence'
  | 'portfolioClarity'
  | 'portfolioOptimisation360'
  | 'expertPersonalisedPlan'
  | 'longTermJourney'
  | 'disciplinedInvesting'
  | 'clearSipActions'
  | 'effortlessSipManagement'
  | 'reviewedEveryMonth'

const PLAN_ICONS_DIR = '/svg/allocation-plans-icons'

type PlanEducationIconProps = {
  color: string
  className?: string
}

export function MaximizingReturnsIcon({ color, className }: PlanEducationIconProps) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path
        d="M4.20786 2.64941H19.7923C19.7923 2.64941 21.3507 2.64941 21.3507 4.20786V19.7923C21.3507 19.7923 21.3507 21.3507 19.7923 21.3507H4.20786C4.20786 21.3507 2.64941 21.3507 2.64941 19.7923V4.20786C2.64941 4.20786 2.64941 2.64941 4.20786 2.64941Z"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M13.5586 8.10352H17.4547V11.9996"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.4546 8.10352L11.2208 14.3373L8.10396 11.2204L2.64941 16.6749"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function TaxOptimisationsIcon({ color, className }: PlanEducationIconProps) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path
        d="M2.64941 15.8965V8.88356C2.64941 7.5925 3.69602 6.5459 4.98708 6.5459H19.0131C20.3041 6.5459 21.3507 7.5925 21.3507 8.88356V15.8965C21.3507 17.1876 20.3041 18.2342 19.0131 18.2342H4.98708C3.69602 18.2342 2.64941 17.1876 2.64941 15.8965Z"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.88281 6.54552V5.7663C8.88281 4.04489 10.2783 2.64941 11.9997 2.64941C13.7211 2.64941 15.1166 4.04489 15.1166 5.7663V6.54552"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.3252 6.5459V18.2342"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16.6758 6.5459V18.2342"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.64941 22.8516H21.3507"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function QuarterlyPlanIcon({ color, className }: PlanEducationIconProps) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path
        d="M12 22.1299C17.5946 22.1299 22.1299 17.5946 22.1299 12C22.1299 6.40542 17.5946 1.87012 12 1.87012C6.40542 1.87012 1.87012 6.40542 1.87012 12C1.87012 17.5946 6.40542 22.1299 12 22.1299Z"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 1.87012V12L19.1688 19.1688"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function PlanEducationIcon({
  iconId,
  color,
  className
}: {
  iconId: PlanEducationIconId
  color: string
  className?: string
}): ReactNode {
  switch (iconId) {
    case 'maximizingReturns':
      return <MaximizingReturnsIcon color={color} className={className} />
    case 'taxOptimisations':
      return <TaxOptimisationsIcon color={color} className={className} />
    case 'quarterlyPlan':
      return <QuarterlyPlanIcon color={color} className={className} />
    case 'investWithConfidence':
      return <PlanIconImage fileName="invest-with-confidence.svg" color={color} className={className} />
    case 'portfolioClarity':
      return <PlanIconImage fileName="portfolio-clarity.svg" color={color} className={className} />
    case 'portfolioOptimisation360':
      return (
        <PlanIconImage fileName="portfolio-optimisation-360.svg" color={color} className={className} />
      )
    case 'expertPersonalisedPlan':
      return <PlanIconImage fileName="expert-personalised-plan.svg" color={color} className={className} />
    case 'longTermJourney':
      return <PlanIconImage fileName="long-term-journey.svg" color={color} className={className} />
    case 'disciplinedInvesting':
      return <PlanIconImage fileName="disciplined-investing.svg" color={color} className={className} />
    case 'clearSipActions':
      return <PlanIconImage fileName="clear-sip-actions.svg" color={color} className={className} />
    case 'effortlessSipManagement':
      return (
        <PlanIconImage fileName="effortless-sip-management.svg" color={color} className={className} />
      )
    case 'reviewedEveryMonth':
      return <PlanIconImage fileName="reviewed-every-month.svg" color={color} className={className} />
    default: {
      const unhandledIconId: never = iconId
      return unhandledIconId
    }
  }
}

/** Mask so file SVGs take the panel accent instead of baked-in purple/blue strokes. */
function PlanIconImage({
  fileName,
  color,
  className
}: {
  fileName: string
  color: string
  className?: string
}) {
  const maskUrl = `url(${PLAN_ICONS_DIR}/${fileName})`
  return (
    <span
      aria-hidden
      className={className}
      style={{
        display: 'inline-block',
        backgroundColor: color,
        WebkitMaskImage: maskUrl,
        maskImage: maskUrl,
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center'
      }}
    />
  )
}
